import fs from "node:fs";

import path from "node:path";
import { downloadImage, getFilenameFromUrl, response, success, TaskState } from "@iflyrpa/share";
import sizeOf from "image-size";
import { AxiosProxyParams, errorResponse, MockPublish, Xiaohongshu } from "../../types";
import { stringifyError } from "../../utils";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import { Xhshow } from "../../utils/xhs_ob_feed";
import type { PublishAction } from ".";

import Topic = Xiaohongshu.Topic;

interface CommonResponse<T = unknown> {
    msg: string;
    data: T;
    proxyInfo?: Record<string, unknown>;
    code: number;
    success: boolean;
    result?: number;
}

interface UploadTempPermit<T = string[]> {
    bucket: string;
    uploadAddr: string;
    fileIds: T;
    token: string;
}

const errnoMap: Record<number, string> = {
    "-1": "未知拦截器错误，请稍后重试。",
    915: "所属C端账号手机号被修改，请重新登录",
    914: "所属C端账号密码被修改，请重新登录",
    903: "账户已登出，需重新登陆重试！",
    902: "登录已过期，请重新登录！",
    906: "账号存在风险，请重新登录！",
    "-9136": "因违反社区规范禁止发笔记，请检查账号状态！",
};

// 小红书参数加密器
const xsEncrypt = new Xhshow();

export const mockAction: PublishAction = async (task, params) => {
    const tmpCachePath = task.getTmpPath();
    if (
        params?.selfDeclaration?.type === "source-statement" &&
        params.selfDeclaration.childType === "transshipment" &&
        params.originalBind
    ) {
        return { code: 414, message: "原创声明与转载声明互斥，请重新选择后发布！", data: "" };
    }
    const a1Cookie = params.cookies.find((it) => it.name === "a1")?.value;
    if (!a1Cookie) {
        return { code: 414, message: "账号数据异常，请重新绑定账号后重试。", data: "" };
    }
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://creator.xiaohongshu.com",
        referer: "https://creator.xiaohongshu.com/",
        "user-agent":
            "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/130.0.0.0 Safari/537.36",
    };
    const recordCookie = params.cookies.reduce<Record<string, string>>((acc, cookie) => {
        if (cookie.name && cookie.value) {
            acc[cookie.name] = cookie.value;
        }
        return acc;
    }, {});

    const args: AxiosProxyParams = [
        { headers },
        task.logger,
        params.proxyLoc,
        params.localIP,
        // params.huiwenToken,
        params.accountId,
        "xiaohongshu", // 业务标识
    ];

    const http = new Http({ headers });
    const proxyHttp = new Http(...args);

    // 添加响应拦截器
    proxyHttp.addResponseInterceptor<CommonResponse>((response) => {
        const responseData = response?.data;

        // 检查响应中的 errno 字段
        if (
            (response && responseData?.code && responseData.code !== 0) ||
            (responseData?.code && !responseData.success)
        ) {
            const errmsg =
                errnoMap[responseData.code] || response.config.defaultErrorMsg || "文章发布异常，请稍后重试。";

            return {
                code: errnoMap[responseData.code] ? 414 : 500,
                message: errmsg,
                data: responseData,
            };
        }
    });

    // 0. 获取上传 token
    const fetchCoverUrl = "/api/media/v1/upload/creator/permit";
    const fetchCoverParams = {
        biz_name: "spectrum",
        scene: "image",
        file_count: params.banners.length,
        version: 1,
        source: "web",
    };
    const fetchCoverXsHeader = xsEncrypt.signHeadersGet(fetchCoverUrl, recordCookie, "ugc", fetchCoverParams);

    const uploadInfos: UploadTempPermit<string>[] = [];
    if (params.banners.length > 18) {
        // 分批次获取 coverId
        const num = Math.ceil(params.banners.length / 14);
        for (let i = 0; i < num; i++) {
            // const start = i * 14;
            // const end = Math.min(start + 14, params.banners.length);
            // const batchParams = {
            //     ...fetchCoverParams,
            //     file_count: end - start,
            // };
            const batchCoverIdInfo = await proxyHttp.api<CommonResponse<{ uploadTempPermits: UploadTempPermit[] }>>(
                {
                    method: "get",
                    baseURL: "https://creator.xiaohongshu.com",
                    url: fetchCoverUrl,
                    defaultErrorMsg: "获取小红书用户信息失败，请稍后重试发布。",
                    headers: fetchCoverXsHeader,
                    params: fetchCoverParams,
                },
                { retries: 3, retryDelay: 20, timeout: 3000 },
            );
            for (const item of batchCoverIdInfo?.data?.uploadTempPermits ?? []) {
                for (const fileId of item.fileIds) {
                    uploadInfos.push({
                        bucket: item.bucket || "",
                        uploadAddr: item.uploadAddr || "",
                        fileIds: fileId,
                        token: item.token || "",
                    });
                }
            }
        }
    } else {
        const coverIdInfo = await proxyHttp.api<CommonResponse<{ uploadTempPermits: UploadTempPermit[] }>>(
            {
                method: "get",
                baseURL: "https://creator.xiaohongshu.com",
                url: fetchCoverUrl,
                defaultErrorMsg: "获取小红书用户信息失败，请稍后重试发布。",
                headers: fetchCoverXsHeader,
                params: fetchCoverParams,
            },
            { retries: 3, retryDelay: 20, timeout: 3000 },
        );
        for (const item of coverIdInfo?.data?.uploadTempPermits ?? []) {
            for (const fileId of item.fileIds) {
                uploadInfos.push({
                    bucket: item.bucket || "",
                    uploadAddr: item.uploadAddr || "",
                    fileIds: fileId,
                    token: item.token || "",
                });
            }
        }
    }

    if (uploadInfos.length < params.banners.length)
        return { code: 414, message: "可用 OSS bucket 数量不足，无法上传所有图片！", data: "" };

    if (uploadInfos.length === 0) return { code: 414, message: "图片上传失败，请稍后重试！", data: "" };

    const availableBuckets = Array.from({ length: uploadInfos.length }, (_, i) => i);

    const uploadFile = async (
        url: string,
        index: number,
    ): Promise<{
        ossFileId: string;
        ossToken: string;
        width: number;
        height: number;
        originSize: number;
    }> => {
        const fileName = getFilenameFromUrl(url);
        const localUrl = await downloadImage(url, path.join(tmpCachePath, fileName));
        const fileBuffer = fs.readFileSync(localUrl);

        // 解析图片宽高与原始大小（origin_size 单位为 KB，与小红书 web 端保持一致）
        let width = 0;
        let height = 0;
        try {
            const dimensions = sizeOf(fileBuffer);
            width = dimensions.width ?? 0;
            height = dimensions.height ?? 0;

            // 根据 EXIF orientation 调整宽高（小红书使用的是旋转后的显示尺寸）
            // orientation 值说明：https://exiftool.sourceforge.net/TagNames/EXIF.html
            // 1 = Horizontal (normal)
            // 2 = Mirror horizontal
            // 3 = Rotate 180
            // 4 = Mirror vertical
            // 5 = Mirror horizontal and rotate 270 CW
            // 6 = Rotate 90 CW
            // 7 = Mirror horizontal and rotate 90 CW
            // 8 = Rotate 270 CW
            const orientation = dimensions.orientation ?? 1;
            if ([5, 6, 7, 8].includes(orientation)) {
                [width, height] = [height, width];
            }
        } catch {
            // 解析失败时保持 0，不阻断上传
        }
        const originSize = fileBuffer.byteLength / 1024;

        const MAX_RETRIES = uploadInfos.length;
        let attempt = 0;

        while (attempt < MAX_RETRIES) {
            // 分配 bucket
            const ossBucketIndex = availableBuckets.shift(); // 移除并获取第一个可用 bucket

            if (ossBucketIndex === undefined) {
                return { ossFileId: "", ossToken: "", width, height, originSize }; // 没有可用 bucket
            }

            const currentInfo = uploadInfos[ossBucketIndex];
            const ossFileId = currentInfo.fileIds;
            const ossToken = currentInfo.token;
            const ossDomain = currentInfo.uploadAddr;

            try {
                await http.api({
                    method: "put",
                    url: `https://${ossDomain}/${ossFileId}`,
                    data: fileBuffer,
                    headers: {
                        "x-cos-security-token": ossToken,
                    },
                    defaultErrorMsg: "图片上传异常，请稍后重试发布。",
                });
                return { ossFileId, ossToken, width, height, originSize };
            } catch (error) {
                attempt++;
            }
        }
        return { ossFileId: "", ossToken: "", width, height, originSize }; // 所有尝试失败
    };

    const coverInfos = await Promise.all(params.banners.map((it, idx) => uploadFile(it, idx)));
    const invalidUpload = coverInfos.find((it) => it.ossToken === "" || it.ossFileId === "");
    if (invalidUpload) return { code: 414, message: "图片上传异常，请稍后重试发布。", data: "" };

    if (params.topic && params.topic.length > 0) {
        await Promise.all(
            params.topic.map(async (topic) => {
                if (!topic.id) {
                    const topicData = { topic_names: topic.name };
                    const topicXsHeader = xsEncrypt.signHeadersPost(
                        "/web_api/sns/capa/postgw/topic/batch_customized",
                        recordCookie,
                        "ugc",
                        topicData,
                    );

                    const createTopic = await proxyHttp.api<{ data?: { topic_infos: Topic[] } }>(
                        {
                            method: "POST",
                            url: "https://edith.xiaohongshu.com/web_api/sns/capa/postgw/topic/batch_customized",
                            headers: topicXsHeader,
                            data: topicData,
                            defaultErrorMsg: "话题创建异常，请稍后重试。",
                        },
                        { retries: 3, retryDelay: 20, timeout: 3000 },
                    );
                    if (!createTopic?.data) throw errorResponse("话题创建失败，请检查话题字段！", 414);
                    Object.assign(topic, createTopic?.data?.topic_infos[0]);
                }
            }),
        );
    }

    const visibleRangeMap = {
        public: 0,
        private: 1,
        friends: 4,
    };

    const publishData = {
        common: {
            ats: [],
            biz_relations: [],
            desc: params?.content,
            goods_info: {},
            hash_tag: params.topic || [],
            note_id: "",
            source: JSON.stringify({
                type: "web",
                ids: "",
                extraInfo: '{"systemId":"web"}',
            }),
            title: params?.title,
            type: "normal",
            privacy_info: {
                op_type: 1,
                type: visibleRangeMap[params.visibleRange],
            },
            post_loc: params.address
                ? {
                      name: params.address?.name,
                      poi_id: params.address?.poi_id,
                      poi_type: params.address?.poi_type,
                      subname: params.address?.full_address,
                  }
                : null,
            business_binds: "",
        },
        image_info: {
            images: coverInfos.map((it) => ({
                extra_info_json: JSON.stringify({
                    mimeType: "image/png",
                    image_metadata: {
                        bg_color: "",
                        origin_size: it.originSize,
                    },
                }),
                file_id: it.ossFileId,
                width: it.width,
                height: it.height,
                metadata: { source: -1 },
                stickers: { floating: [], version: 2 },
            })),
        },
        video_info: null,
    };

    // 自主声明
    const userDeclarationBind = {
        origin: 2,
        photoInfo: {},
        repostInfo: {},
    };

    if (params.selfDeclaration?.type === "fictional-rendition") {
        // 虚拟演绎，仅供娱乐
        userDeclarationBind.origin = 1;
    } else if (params.selfDeclaration?.type === "ai-generated") {
        // 笔记含AI合成内容
        userDeclarationBind.origin = 2;
    } else if (params.selfDeclaration?.type === "source-statement") {
        // 内容来源声明
        if (params.selfDeclaration.childType === "self-labeling") {
            // 已自主标注
            userDeclarationBind.origin = 3;
        } else if (params.selfDeclaration.childType === "self-shooting") {
            // 自主拍摄
            userDeclarationBind.origin = 4;
            const photoInfo: { photoPlace?: unknown; photoTime?: string } = {};

            if (params.selfDeclaration.shootingLocation) {
                // 拍摄地点
                photoInfo.photoPlace = {
                    name: params.selfDeclaration.shootingLocation.name,
                    poiId: params.selfDeclaration.shootingLocation.poi_id,
                    poiType: params.selfDeclaration.shootingLocation.poi_type,
                    subname: params.selfDeclaration.shootingLocation.full_address,
                };
            }

            if (params.selfDeclaration.shootingDate) {
                // 拍摄日期
                photoInfo.photoTime = params.selfDeclaration.shootingDate;
            }

            userDeclarationBind.photoInfo = photoInfo;
        } else if (params.selfDeclaration.childType === "transshipment") {
            // 来源转载
            userDeclarationBind.origin = 5;

            if (params.selfDeclaration.sourceMedia) {
                // 来源媒体
                userDeclarationBind.repostInfo = {
                    source: params.selfDeclaration.sourceMedia,
                };
            }
        }
    }

    const bizId =
        params?.originalBind &&
        (params.cookies.find((it) => it.name === "x-user-id-creator.xiaohongshu.com")?.value ||
            (await proxyHttp
                .api<{ data: { userDetail: { id: string } } }>(
                    {
                        method: "get",
                        url: "https://creator.xiaohongshu.com/api/galaxy/user/my-info",
                    },
                    { retries: 3, retryDelay: 20, timeout: 3000 },
                )
                .then((userData) => userData?.data?.userDetail?.id)));

    const business_binds = {
        version: 1,
        bizType: "",
        noteId: "",
        noteOrderBind: {},
        // 发布时间 - 立即发布/定时发布
        notePostTiming: params.isImmediatelyPublish ? {} : { postTime: params.scheduledPublish },
        coProduceBind: { enable: !!params?.coProduceBind }, // 是否允许合拍
        noteCopyBind: { copyable: !!params?.noteCopyBind }, // 关闭允许复制
        optionRelationList: params.originalBind
            ? [
                  {
                      // 原创声明
                      type: "ORIGINAL_STATEMENT",
                      relationList: [
                          {
                              bizId,
                              bizType: "ORIGINAL_STATEMENT",
                              extraInfo: "{}",
                          },
                      ],
                  },
              ]
            : [],
        ...(params?.groupBind
            ? {
                  groupBind: {
                      groupId: params?.groupBind?.group_id,
                      groupName: params.groupBind?.group_name,
                      desc: params.groupBind?.desc,
                      avatar: params.groupBind?.avatar,
                  },
              }
            : { groupBind: {} }),
        ...(params.selfDeclaration ? { userDeclarationBind } : {}),
        ...(params?.collectionBind?.id ? { noteCollectionBind: { id: params.collectionBind.id } } : {}),
    };

    publishData.common.business_binds = JSON.stringify(business_binds);

    const publishXsHeader = xsEncrypt.signHeadersPost("/web_api/sns/v2/note", recordCookie, "ugc", publishData);

    task._timerRecord.PrePublish = Date.now();

    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    if (MockPublish) {
        const message = `文章模拟发布成功 ${http.proxyInfo}`;
        const data = "123456789";

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return success(data, message);
    }

    // task.logger.info(`[xiaohongshuPublish] publishData: ${JSON.stringify(publishData)} `);

    // 发布
    let publishResult: CommonResponse<{ id: string }>;
    try {
        publishResult = await proxyHttp.api<CommonResponse<{ id: string }>>(
            {
                method: "post",
                url: "https://edith.xiaohongshu.com/web_api/sns/v2/note",
                data: publishData,
                headers: publishXsHeader,
                defaultErrorMsg: "文章发布异常，请稍后重试。",
            },
            { retries: 2, retryDelay: 500, timeout: 12 * 1000 },
        );
    } catch (error) {
        const handledError = Http.handleApiError(error);
        // 代理连接超时(599) 与网络/服务端错误(500/502/503/504)：重试耗尽后直接返回代理错误信息，
        // 不再 throw 走 executeAction 的 RPA 兜底。code 归一为 414，使 executeAction 视为终止结果。
        const isProxyOrNetworkError = [599, 500, 502, 503, 504].includes(handledError.code);
        if (isProxyOrNetworkError) {
            // 区分代理错误和普通网络错误
            const isProxyRequest = handledError.extra?.isProxyRequest === true;
            const errorType = handledError.code === 599 || isProxyRequest ? "代理错误" : "网络错误";
            const message = `文章发布失败，${errorType}：${handledError.message}${task.debug ? ` ${http.proxyInfo}` : ""}`;
            task.logger.error(`[xiaohongshuPublish] ${errorType}，直接返回: ${message}`, stringifyError(handledError));
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: message,
            });
            return { code: 414, data: "", message };
        }
        // 其它错误码维持原有逻辑，抛出以便上层 executeAction 走 RPA 兜底
        throw error;
    }

    reportLogger({
        token: params.huiwenToken || "",
        enverionment: task.enverionment || "development",
        postId: params.articleId,
        eip: proxyHttp.proxyInfo,
        proxyIp: params.localIP,
        accountId: params.accountId,
        uid: params.uid,
        platform: "xiaohongshu",
        publishParams: publishData,
    });

    task.logger.info(`[xiaohongshuPublish] publishResult: ${JSON.stringify(publishResult)} `);

    const isSuccess = publishResult.success;
    const message = `文章发布${isSuccess ? "成功" : `失败，原因：${publishResult.msg}`}${task.debug ? ` ${http.proxyInfo}` : ""}`;
    const data = isSuccess ? publishResult?.data?.id || "" : "";

    if (!isSuccess) {
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: message,
        });
        return { code: 414, data, message };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: data,
        },
    });
    // 临时返回结束
    return response(isSuccess ? 0 : 414, message, data);
};
